/**
 * Localized copy for the shared DOM game-state overlay
 * (`@bn-games/ui-shell`'s `createGameStateOverlay`) — mahjong's own, since each
 * game owns its dictionary.
 *
 * One mahjong-specific departure from checkers'/backgammon's wording: **no
 * opponent is named.** A four-seat table has three of them and the wire's
 * `OPPONENT_DISCONNECTED` carries no seat, so naming one would mean guessing.
 * The `name` argument the shared type passes is therefore ignored throughout.
 */

import type { GameStateOverlayLabels } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';

export function buildOnlineOverlayLabels(): GameStateOverlayLabels {
  return {
    opponentDisconnected: (_name, countdown) => ({
      title: t('online.overlay.playerDisconnected'),
      body: t('online.overlay.waitingForReconnection', { countdown }),
    }),
    reconnectingSelf: (countdown) => ({
      title: t('online.overlay.reconnecting'),
      body: t('online.overlay.restoringGame', { countdown }),
    }),
    gameDisconnected: () => ({
      title: t('online.overlay.connectionLost'),
      body: t('online.overlay.gameEnded'),
    }),
    rematchOffered: () => ({
      title: t('online.overlay.rematchOffered'),
      body: t('online.overlay.waitingForOthers'),
    }),
    rematchRequested: () => ({
      title: t('online.overlay.rematchRequested'),
      body: t('online.overlay.rematchPrompt'),
    }),
    rematchDeclined: () => ({ title: t('online.overlay.rematchDeclined') }),
    gameOver: (result) => ({
      title:
        result === 'win'
          ? t('online.overlay.gameOverWin')
          : result === 'loss'
            ? t('online.overlay.gameOverLoss')
            : t('online.overlay.gameOverDraw'),
      body: t('online.overlay.gameOverPrompt'),
    }),
    buttons: {
      leave: t('online.overlay.buttons.leave'),
      rematch: t('online.overlay.buttons.rematch'),
      'return-to-menu': t('online.overlay.buttons.returnToMenu'),
      cancel: t('online.overlay.buttons.cancel'),
      accept: t('online.overlay.buttons.accept'),
      decline: t('online.overlay.buttons.decline'),
    },
  };
}
